/**
 * Silent refresh — régénère un access token via le cookie HTTP-only
 * `refresh_token`, **avec déduplication** des requêtes simultanées.
 *
 * @remarks
 * **Pourquoi la déduplication** : quand l'access token expire, plusieurs
 * requêtes peuvent se voir refuser en 401 simultanément. Sans dédup, chacune
 * lancerait son propre POST `/api/auth/refresh`. C'est inutile (1 seul est
 * nécessaire pour récupérer un nouveau token) et dangereux (race conditions
 * sur la rotation côté serveur).
 *
 * Cette implémentation maintient une `pendingRefresh: Promise<boolean> | null`.
 * Si N appels arrivent pendant qu'un refresh est en cours, ils attendent tous
 * la même promise. Quand elle résout, ils repartent tous avec le nouveau token.
 *
 * **Retour** : `boolean` — `true` si refresh réussi, `false` si échec
 * (session vraiment expirée, l'appelant doit logout).
 *
 * **Imports** : on importe dynamiquement les modules pour éviter une
 * dépendance circulaire `lib/http/client → lib/auth/refresh → features/auth`.
 *
 * @example
 * ```ts
 * // Dans un loader / interceptor
 * const ok = await attemptSilentRefresh();
 * if (!ok) return redirect('/login');
 * ```
 */

import type { RefreshTokenResponse } from '@/lib/http/types';

let pendingRefresh: Promise<boolean> | null = null;

export async function attemptSilentRefresh(): Promise<boolean> {
  if (pendingRefresh !== null) {
    // Une refresh est déjà en cours — on attend la même promise.
    return pendingRefresh;
  }

  pendingRefresh = (async (): Promise<boolean> => {
    try {
      // Imports dynamiques pour casser la dépendance circulaire potentielle
      // (`apiClient` importe `useAuthStore`, qu'on importe aussi ici).
      const [{ refreshToken }, { useAuthStore }] = await Promise.all([
        import('@/features/auth/api/auth'),
        import('@/app/stores/use-auth-store'),
      ]);

      const data: RefreshTokenResponse = await refreshToken();
      useAuthStore.getState().setSession(data);
      return true;
    } catch {
      // 401 / 403 / network error → la session est vraiment finie.
      const { useAuthStore } = await import('@/app/stores/use-auth-store');
      useAuthStore.getState().logout({ expired: true });
      return false;
    } finally {
      pendingRefresh = null;
    }
  })();

  return pendingRefresh;
}
